import React, { useState, useEffect } from 'react';
import { FiSave, FiClock, FiX, FiEdit3, FiInfo, FiSmartphone, FiCalendar } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Api from '../../Api';
import './style.css';

export default function EditPointModal({ isOpen, onClose, pointData, companyId, onSave }) {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  
  // Form State
  const [times, setTimes] = useState({
    entry1: '',
    exit1: '',
    entry2: '',
    exit2: ''
  });

  useEffect(() => {
    if (isOpen && pointData) {
      // Initialize times from records
      const newTimes = { entry1: '', exit1: '', entry2: '', exit2: '' };
      if (pointData.records) {
        if (pointData.records[0]) newTimes.entry1 = pointData.records[0].time;
        if (pointData.records[1]) newTimes.exit1 = pointData.records[1].time;
        if (pointData.records[2]) newTimes.entry2 = pointData.records[2].time;
        if (pointData.records[3]) newTimes.exit2 = pointData.records[3].time;
      }
      setTimes(newTimes);

      // Load History
      loadHistory();
    }
  }, [isOpen, pointData]);

  const loadHistory = async () => {
    if (!pointData) return;
    
    setHistoryLoading(true);
    try {
      const history = await Api.getPointHistory(companyId, pointData.employee_id, pointData.competence_date);
      setAdjustments(history);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Erro ao carregar histórico de edições');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    setTimes(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      // Construct requested_times array
      const requestedTimes = [
        times.entry1,
        times.exit1,
        times.entry2,
        times.exit2
      ].filter(t => t); // Remove empty strings

      const payload = {
        employeeId: pointData.employee_id,
        date: pointData.competence_date,
        records: requestedTimes.map(t => ({ time: t })),
        reason: '' // Reason removed as per user request
      };

      await Api.editDailyPoints(companyId, pointData.employee_id, payload);
      toast.success('Ponto editado com sucesso!');
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error creating adjustment:', error);
      toast.error('Erro ao salvar edição');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderHistoryCard = (item, index) => {
    // Parse new_records to get the times
    const records = Array.isArray(item.new_records) ? item.new_records : [];
    const times = {
      entry1: records[0]?.time || '-',
      exit1: records[1]?.time || '-',
      entry2: records[2]?.time || '-',
      exit2: records[3]?.time || '-'
    };

    return (
      <div key={item.id} className="history-card">
        <div className="history-header">
          <div className={`history-badge ${index === adjustments.length - 1 ? 'original' : 'edited'}`}>
            {index === adjustments.length - 1 ? 'REGISTRO ORIGINAL' : 'EDITADO'}
          </div>
          <div className="history-date">
            <FiCalendar />
            {new Date(item.createdAt).toLocaleDateString('pt-BR')}
          </div>
        </div>

        <div className="history-times-grid">
          <div className="history-time-block">
            <span className="history-time-label">ENTRADA</span>
            <span className="history-time-value">{times.entry1}</span>
          </div>
          <div className="history-time-block">
            <span className="history-time-label">SAÍDA</span>
            <span className="history-time-value">{times.exit1}</span>
          </div>
          <div className="history-time-block">
            <span className="history-time-label">ENTRADA</span>
            <span className="history-time-value">{times.entry2}</span>
          </div>
          <div className="history-time-block">
            <span className="history-time-label">SAÍDA</span>
            <span className="history-time-value">{times.exit2}</span>
          </div>
        </div>

        <div className="history-footer">
          <FiSmartphone />
          {item.edited_by_name || 'APP PONTO'}
        </div>
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiEdit3 /> Nova Edição de Horário
        </div>
      }
      size="lg"
    >
      <div className="edit-point-modal-content">
        <form onSubmit={handleSubmit} className="edit-point-form">
          
          <div className="time-inputs-grid">
            <Input
              label="1ª ENTRADA"
              name="entry1"
              type="time"
              value={times.entry1}
              onChange={handleTimeChange}
              icon={FiClock}
            />
            <Input
              label="1ª SAÍDA"
              name="exit1"
              type="time"
              value={times.exit1}
              onChange={handleTimeChange}
              icon={FiClock}
            />
            <Input
              label="2ª ENTRADA"
              name="entry2"
              type="time"
              value={times.entry2}
              onChange={handleTimeChange}
              icon={FiClock}
            />
            <Input
              label="2ª SAÍDA"
              name="exit2"
              type="time"
              value={times.exit2}
              onChange={handleTimeChange}
              icon={FiClock}
            />
          </div>

          {/* Warning Box */}
          <div className="warning-box">
            <FiInfo size={20} />
            <div>
              <strong>Atenção:</strong> A nova edição substituirá o registro vigente atual. Esta ação ficará registrada no histórico.
            </div>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
            >
              <FiX /> Cancelar
            </Button>
            <Button type="submit" loading={loading}>
              <FiSave /> Salvar Edição
            </Button>
          </div>
        </form>

        {/* History Section */}
        {adjustments.length > 0 && (
          <div className="history-section">
            <span className="form-section-title">
              <FiClock /> Histórico de Edições
            </span>
            {adjustments.map((item, index) => renderHistoryCard(item, index))}
          </div>
        )}
      </div>
    </Modal>
  );
}
